import { UserDefault } from '../model/user/model'
import { pluralize } from './pluralize'
import { isDefined } from './isEmpty'

export const MILLISECOND = 1
export const SECOND = 1000 * MILLISECOND
export const MINUTE = 60 * SECOND
export const HOUR = 60 * MINUTE
export const DAY = 24 * HOUR
export const WEEK = 7 * DAY
export const MONTH = 30 * DAY
export const YEAR = 365 * DAY
export const EVENT_BRIDGE_AT_SCHEDULE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss"

/** Delay before a conversation is available in Skill Share after being auto-shared */
export const SKILL_SHARE_DELAY = 1 * HOUR

export function daysSince(time: number): number {
  return (Date.now() - time) / DAY
}

export function secondsToReadableDuration(durationSeconds: number): string {
  const durationMilliseconds = durationSeconds * 1000
  if (durationMilliseconds < MINUTE) {
    const seconds = Math.round(durationMilliseconds / SECOND)
    return `${seconds} second${seconds !== 1 ? 's' : ''}`
  } else if (durationMilliseconds < HOUR) {
    const minutes = Math.round(durationMilliseconds / MINUTE)
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  } else if (durationMilliseconds < DAY) {
    const hours = Math.round(durationMilliseconds / HOUR)
    return `${hours} hour${hours !== 1 ? 's' : ''}`
  } else {
    const days = Math.round(durationMilliseconds / DAY)
    return `${days} day${days !== 1 ? 's' : ''}`
  }
}

export function secondsToShortDuration(durationSeconds: number): string {
  const durationMilliseconds = durationSeconds * SECOND
  const { days, hours, minutes, seconds } = calculateDuration(durationMilliseconds)
  if (durationMilliseconds < MINUTE) {
    return `${seconds}s`
  } else if (durationMilliseconds < HOUR) {
    return `${minutes}m ${seconds}s`
  } else if (durationMilliseconds < DAY) {
    return `${hours}h ${minutes}m`
  } else {
    return `${days}d ${hours}h`
  }
}

// We could get ALL the timezones, but we only a few in the US, so I'm just going to simplify this.
export const US_TIME_ZONES = new Set<string>([
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
])

export const DEFAULT_TIMEZONE = UserDefault.timeZone

export type ParsedDuration = {
  years: number
  months: number
  days: number
  hours: number
  minutes: number
  seconds?: number
}

export const calculateDuration = (milliseconds: number): ParsedDuration => {
  let remaining = milliseconds

  const years = Math.floor(remaining / YEAR)
  remaining %= YEAR

  const months = Math.floor(remaining / MONTH)
  remaining %= MONTH

  const days = Math.floor(remaining / DAY)
  remaining %= DAY

  const hours = Math.floor(remaining / HOUR)
  remaining %= HOUR

  const minutes = Math.floor(remaining / MINUTE)
  remaining %= MINUTE

  const seconds = Math.floor(remaining / SECOND)

  return { years, months, days, hours, minutes, seconds }
}

export const calculateMilliseconds = (duration: ParsedDuration): number => {
  return (
    duration.years * YEAR +
    duration.months * MONTH +
    duration.days * DAY +
    duration.hours * HOUR +
    duration.minutes * MINUTE +
    (duration.seconds ?? 0) * SECOND
  )
}

export const stringifyDurationShort = (duration: ParsedDuration): string => {
  const { days, hours, minutes, seconds } = duration
  const parts = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  if (isDefined(seconds) && seconds > 0) parts.push(`${seconds}s`)
  return parts.join(' ')
}

export const stringifyDuration = (duration: ParsedDuration): string => {
  const { years, months, days, hours, minutes } = duration
  const parts = []
  if (years > 0) parts.push(`${years} ${pluralize(years, 'year')}`)
  if (months > 0) parts.push(`${months} ${pluralize(months, 'month')}`)
  if (days > 0) parts.push(`${days} ${pluralize(days, 'day')}`)
  if (hours > 0) parts.push(`${hours} ${pluralize(hours, 'hour')}`)
  if (minutes > 0) parts.push(`${minutes} ${pluralize(minutes, 'minute')}`)
  return parts.join(', ')
}

export function toIsoDateInTimezone(time: number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(time)
  const findPart = (type: Intl.DateTimeFormatPartTypes): string => parts.find((part) => part.type === type)?.value ?? ''
  return `${findPart('year')}-${findPart('month')}-${findPart('day')}`
}
